/**
 * Plain-language names and color reading for farm indices.
 * Color notes follow the palettes in digisaka-webgis-fastapi `app/core/satellite_indices.py`.
 */

export type IndexReadingGuide = {
  /** Dropdown text, e.g. "NDVI: crop greenness". */
  shortLabel: string;
  /** Short phrase after the code, e.g. "crop greenness". */
  meaning: string;
  /** When a farmer would open this map. */
  use: string;
  /** How to read the colors on this map. */
  colors: string;
};

const GREENNESS_COLORS =
  'Colors run from red (low) through yellow to green (high). Red and orange mean little green crop, bare soil, or stress. Green means a fuller, healthier canopy.';

const GUIDE: Record<string, IndexReadingGuide> = {
  ndvi: {
    shortLabel: 'NDVI: crop greenness',
    meaning: 'crop greenness',
    use: 'The everyday map for “how green is my crop?” Use it to compare fields and to see if a block is thinning.',
    colors: GREENNESS_COLORS,
  },
  evi: {
    shortLabel: 'EVI: crop greenness',
    meaning: 'crop greenness',
    use: 'Like NDVI, but steadier in very dense canopy. Use it when a lush field looks “maxed out” on NDVI.',
    colors: GREENNESS_COLORS,
  },
  msavi2: {
    shortLabel: 'MSAVI2: greenness on bare soil',
    meaning: 'greenness on bare soil',
    use: 'Best early in the season, when soil still shows between plants. It reduces the false reading from bare ground.',
    colors: GREENNESS_COLORS,
  },
  savi: {
    shortLabel: 'SAVI: greenness, soil adjusted',
    meaning: 'greenness, soil adjusted',
    use: 'Use when the field is patchy and soil is visible. It asks the same greenness question as NDVI, with less soil noise.',
    colors: GREENNESS_COLORS,
  },
  vari: {
    shortLabel: 'VARI: visible greenness',
    meaning: 'visible greenness',
    use: 'A simple greenness check from colors you can see (green, red, blue). Handy when you want a quick canopy look.',
    colors: GREENNESS_COLORS,
  },
  arvi: {
    shortLabel: 'ARVI: greenness through haze',
    meaning: 'greenness through haze',
    use: 'Use on hazy or smoky days when a normal greenness map looks washed out. It corrects for that atmosphere.',
    colors: GREENNESS_COLORS,
  },
  rendvi: {
    shortLabel: 'RENDVI: red-edge greenness',
    meaning: 'red-edge greenness',
    use: 'A finer greenness check that notices small changes in the canopy before they show up on NDVI.',
    colors: GREENNESS_COLORS,
  },
  reb_ndvi1: {
    shortLabel: 'RE NDVI: red-edge greenness',
    meaning: 'red-edge greenness',
    use: 'Another red-edge greenness view. Use it to spot early thinning inside an otherwise green field.',
    colors: GREENNESS_COLORS,
  },
  sipi: {
    shortLabel: 'SIPI: leaf pigment stress',
    meaning: 'leaf pigment stress',
    use: 'Use when you suspect the crop is losing healthy green pigment — stress, pests, or aging leaves.',
    colors:
      'Colors run from dark blue (low) to bright yellow (high). Darker means a healthier pigment balance. Yellow means the leaves are shifting away from green, often stress or aging.',
  },
  rgr: {
    shortLabel: 'RGR: red-to-green ratio',
    meaning: 'red-to-green ratio',
    use: 'Compares red light to green light. Use it to see where the crop is less green than its neighbors.',
    colors:
      'Colors run from pale (low) to dark red (high). Pale means more green crop. Dark red means the canopy is less green.',
  },
  psri: {
    shortLabel: 'PSRI: crop aging',
    meaning: 'crop aging',
    use: 'Use near harvest or when you think the crop is drying down. It rises as leaves senesce.',
    colors:
      'Colors run from red (low) to pale yellow (high). Higher, yellower areas are aging or drying faster than the redder, lower areas.',
  },
  ndii: {
    shortLabel: 'NDII: canopy water',
    meaning: 'canopy water',
    use: 'Shows water held in the leaves. Use it after dry spells to see which blocks are drying first.',
    colors:
      'Colors run from red (low) to dark green (high). Red means a dry canopy. Green means the leaves are holding more water.',
  },
  ndmi: {
    shortLabel: 'NDMI: crop moisture',
    meaning: 'crop moisture',
    use: 'The moisture map. Use it to see wet and dry parts of the field, not how green the crop is.',
    colors:
      'Colors run from dark red (dry) through gold to blue (wet). Red and brown mean a dry crop. Green to blue means more moisture in the canopy.',
  },
  ndwi: {
    shortLabel: 'NDWI: surface water',
    meaning: 'surface water',
    use: 'Finds open water and very wet ground. Use it after floods or to see standing water, not crop health.',
    colors:
      'Colors run from red (land and crop) to dark blue (water). Blue patches are wet or flooded. Red and yellow are drier ground.',
  },
  nbr: {
    shortLabel: 'NBR: burn signal',
    meaning: 'burn signal',
    use: 'Use after fire or straw burning to see which parts lost vegetation.',
    colors:
      'Colors run from red (low) to dark green (high). Green means vegetation is still there. Red means bare, burned, or heavily disturbed ground.',
  },
  ireci: {
    shortLabel: 'IRECI: chlorophyll',
    meaning: 'chlorophyll',
    use: 'A closer look at leaf chlorophyll. Use it when two green fields need a finer comparison.',
    colors:
      'Colors run from pale (low) to dark red (high). Darker red means more chlorophyll. Pale means less. On this map, red is the high end, not a warning by itself.',
  },
  s2rep: {
    shortLabel: 'S2REP: red-edge position',
    meaning: 'red-edge position',
    use: 'Tracks a subtle shift in the canopy that often moves with chlorophyll. Use it for early change, not a simple green/brown check.',
    colors:
      'Colors run from red (low) to yellow (high). Yellow means the red edge has shifted, often a stronger canopy. Red is the lower end.',
  },
  sccci: {
    shortLabel: 'SCCCI: canopy chlorophyll',
    meaning: 'canopy chlorophyll',
    use: 'Compares chlorophyll with overall greenness. Use it to see if a green field is actually rich in chlorophyll or just dense.',
    colors:
      'Colors run from green (low) to red (high). Green is the low end and red is the high end. Redder means more chlorophyll relative to the canopy.',
  },
  rvi: {
    shortLabel: 'RVI: radar vegetation',
    meaning: 'radar vegetation',
    use: 'A vegetation check from radar, so clouds do not block it. Use it when optical photos are cloudy.',
    colors:
      'Colors run from blue (low) to red (high). Blue means a weak vegetation signal. Red means a stronger canopy on radar. Red here is not “bad”.',
  },
  ndpi: {
    shortLabel: 'NDPI: radar surface',
    meaning: 'radar surface',
    use: 'Compares two radar channels to see how the surface scatters the signal. Use it to compare texture across the field when photos are cloudy.',
    colors:
      'Colors run from blue (low) through white to red (high). Read it as a scale, not as healthy versus sick. Compare one part of the field with another.',
  },
  radar_soil_moisture: {
    shortLabel: 'Radar soil moisture',
    meaning: 'soil moisture',
    use: 'Estimates moisture from radar, including on cloudy days. Use it to see wetter and drier soil, not leaf greenness.',
    colors:
      'Wetter soil and drier soil show as different ends of the color scale. Compare blocks with each other on the same date rather than chasing one “good” color.',
  },
  sentinel1: {
    shortLabel: 'Sentinel-1: radar signal',
    meaning: 'radar signal',
    use: 'A cloud-proof time series of radar return. Use it when you need a date even if the sky was cloudy.',
    colors:
      'This view is a number in decibels, not a red-to-green health map. Higher or lower than the previous date means the surface return changed.',
  },
};

const FALLBACK: IndexReadingGuide = {
  shortLabel: 'Index value',
  meaning: 'index value',
  use: 'Compare this map across dates and across parts of the field.',
  colors: 'Read the legend on the map. One end is the low value and the other end is the high value.',
};

export function indexReadingGuide(index: string | null | undefined): IndexReadingGuide {
  if (!index) return FALLBACK;
  return GUIDE[index] ?? GUIDE[index.toLowerCase()] ?? FALLBACK;
}

export function indexDropdownLabel(index: string, fallbackLabel?: string): string {
  return indexReadingGuide(index).shortLabel || fallbackLabel || index.toUpperCase();
}
